import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData} from 'wc-lib';
import {format} from 'date-fns';


require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/table.svg');
require('wc-lib/icons/download.svg');
require('wc-lib/icons/graph.svg');
require('wc-lib/icons/infocircle.svg');

@Component({
  template: require('./distribution-of-unserved-energy.html'),
  name: 'distribution-of-unserved-energy',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class DistributionOfUnservedEnergy extends Vue {
  data: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  pivotedData: Array<any> = [];
  chartVisible = true;
  tableVisible = false;
  availableRegions: string[] = [];
  selectedRegion: string = '';
  availablePeriods: string[] = [];
  selectedPeriod: string = '';
  runDateTime: string = "";
  runNo: string = "";
  id: string;
  infoText: string = `This figure shows the distribution of annual unserved energy observed across all simulations alongside the reliability standard. These distributions can be used to determine the likelihood of observing any unserved energy for a given demand POE level, and the likelihood that annual unserved energy would exceed the reliability standard.
 
Probability of Exceedance (POE) means the probability (as a percentage) that a maximum demand forecast will be met or exceeded (for example, due to weather conditions). For example, a 10% POE forecast is expected to be met or exceeded, on average, only one out of every 10 years, so considers more extreme weather than a 50% POE forecast, which is expected to be met or exceeded, on average, one out of every two years.`;
  columns: Array<any> = [
    { title: 'Region', field: 'Region' }, 
    { title: 'Period', field: 'Period' }, 
    { title: 'Percentage', field: 'Percentage' }, 
    { title: 'POE 10', field: 'POE10 USE' }, 
    { title: 'POE 50', field: 'POE50 USE' }, 
    { title: 'Reliability Standard', field: 'ReliabilityStandard' } 
  ];

  get chartButtonType(): string {
    return this.chartVisible ? 'primary' : 'ghost';
  }

  get tableButtonType(): string {
    return this.tableVisible ? 'primary' : 'ghost';
  }

  chartConfig: any = {
    type: 'serial',
    theme: 'simpleblue',
    valueAxes: [{
        id: 'v1',
        position: 'left',
        title: 'Annual Unserved Energy (MWh)',
        labelsEnabled: true
      }],
    graphs: [
    ],
    chartCursor: {
      categoryBalloonEnabled: false,
      cursorAlpha: 0,
      zoomable: false
    },
    categoryAxis: {
      title: 'Percentage of Simulation Runs with Unserved Energy below this level',
      parseDates: false,
      //labelRotation: 45,
      color: '#6F7072',
      gridAlpha: 0.5,
      autoGridCount: true
    },
    legend: {
      equalWidths: true,
      useGraphSettings: false,
      position: 'top',
      valueAlign: 'left'
    },
    categoryField: 'Percentage'
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'REGIONID': this.selectedRegion, 'YEAR': this.selectedPeriod });
    this.data = this.dynamicView.data();
    this.processData();
  }

  processData() {
    let data = this.data;
    let tmpData = [
      { Percentage: 0, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 10, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 20, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 30, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 40, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 50, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 60, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 70, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 80, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 90, Region: this.selectedRegion, Period: this.selectedPeriod },
      { Percentage: 100, Region: this.selectedRegion, Period: this.selectedPeriod }
    ];
    for (let row of data) {
      tmpData[0][`${row.DEMAND_POE_TYPE} USE`] = 0;
      tmpData[1][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE10;
      tmpData[2][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE20;
      tmpData[3][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE30;
      tmpData[4][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE40;
      tmpData[5][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE50;
      tmpData[6][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE60;
      tmpData[7][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE70;
      tmpData[8][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE80;
      tmpData[9][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE90;
      tmpData[10][`${row.DEMAND_POE_TYPE} USE`] = row.USE_PERCENTILE100;
      if (row.DEMAND_POE_TYPE === 'POE10') {
        tmpData[0][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[1][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[2][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[3][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[4][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[5][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[6][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[7][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[8][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[9][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
        tmpData[10][`ReliabilityStandard`] = (0.00002 * row.NATIVEDEMAND).toFixed(2);
      }
    }

    this.pivotedData = tmpData;
  }

  async created() {
    this.id = await DataManager.addEndPoint('../../api/report/MTPASA_CHART_2a', undefined, undefined, 'MTPASA_CHART_2a', undefined, 'yyyy-MM-ddTHH:mm:ss', ['RUN_DATETIME', 'PERIOD_ENDING']);
    this.dynamicView = DataManager.getDynamicView(this.id, 'distributed');
    this.dynamicView.removeFilters();
    let allData = this.dynamicView.data();
    this.runDateTime = format(allData[0].RUN_DATETIME, 'yyyy-MM-dd HH:mm:ss');
    this.runNo = allData[0].RUN_NO;
    let poeTypes = [];
    allData.forEach( x => {
      x.YEAR = format(x.PERIOD_ENDING, 'yyyy');
      if(this.availableRegions.indexOf(x.REGIONID) === -1) {
        this.availableRegions.push(x.REGIONID);
      }
      if(this.availablePeriods.indexOf(x.YEAR) === -1) {
        this.availablePeriods.push(x.YEAR);
      }
      if(poeTypes.indexOf(x.DEMAND_POE_TYPE) === -1) {
        poeTypes.push(x.DEMAND_POE_TYPE);
      }
      this.selectedRegion = x.REGIONID;
      this.selectedPeriod = x.YEAR;
    });

    this.chartConfig.graphs.push({ valueAxis: 'v1', balloonText: '[[title]] : [[value]] MWh', title: `Reliability Standard`, lineThickness:3, valueField: `ReliabilityStandard` });
    for(let poeType of poeTypes) {
      this.chartConfig.graphs.push({ valueAxis: 'v1', balloonText: '[[title]] : [[value]] MWh', title: `${poeType}`, lineThickness:3, valueField: `${poeType} USE` });
    }
    /*for(let period of this.availablePeriods) {
      this.chartConfig.graphs.push({ valueAxis: 'v1', balloonText: '[[title]] : [[value]] MWh', title: `${period} Reliability Standard`, lineThickness:3, valueField: `${period} ReliabilityStandard` });
      for(let poeType of poeTypes) {
        this.chartConfig.graphs.push({ valueAxis: 'v1', balloonText: '[[title]] : [[value]] MWh', title: `${period} ${poeType}`, lineThickness:3, valueField: `${period} ${poeType} USE` });
      }
    }*/

    this.applyFilter();
  }

  @Watch('selectedPeriod')
  onSelectedPeriodChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  chartClick() {
    this.chartVisible = true;
    this.tableVisible = false;
  }

  tableClick() {
    this.tableVisible = true;
    this.chartVisible = false;
  }

  downloadClick() {
    DownloadHelper.download('mtpasa_chart_2a', this.pivotedData, this.columns);
  }
}



// WEBPACK FOOTER //
// src/views/electricity/nem/mtpasa/distribution-of-unserved-energy.ts